/**
 * 多语言 display_text 缺失标记
 *
 * 当 display_text 中任意约定语言（中文/英文/高棉语）缺失或为空时，显示小图标提醒。
 * 用于字典项列表、地区管理列表等，便于运营一眼发现需补全的项。
 */
import { WarningOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React from 'react';

/** 约定校验的语言及其中文名 */
const DEFAULT_LANGS: Array<{ key: string; label: string }> = [
  { key: 'zh_CN', label: '中文' },
  { key: 'en_US', label: '英文' },
  { key: 'km_KH', label: '高棉语' },
];

function isMissing(value: unknown): boolean {
  if (value == null) return true;
  const s = typeof value === 'string' ? value : String(value);
  return s.trim() === '';
}

export interface DisplayTextMissingBadgeProps {
  /** 多语言显示文本，与 API 的 display_text 一致 */
  displayText?: Record<string, any> | null;
  /** 需要校验的语言 key 列表，默认 zh_CN / en_US / km_KH */
  languages?: Array<{ key: string; label: string }>;
  /** 图标样式 */
  style?: React.CSSProperties;
  /** 类名 */
  className?: string;
}

/**
 * 若 display_text 中任意约定语言缺失或为空，渲染一个小图标 + Tooltip 提示缺少哪些语言；否则不渲染。
 */
const DisplayTextMissingBadge: React.FC<DisplayTextMissingBadgeProps> = ({
  displayText,
  languages = DEFAULT_LANGS,
  style,
  className,
}) => {
  const missing = languages.filter(
    (lang) => isMissing(displayText?.[lang.key]),
  );
  if (missing.length === 0) return null;

  const tip = `缺少多语言：${missing.map((m) => m.label).join('、')}`;
  return (
    <Tooltip title={tip}>
      <WarningOutlined
        style={{ color: '#faad14', marginLeft: 4, ...style }}
        className={className}
      />
    </Tooltip>
  );
};

export default DisplayTextMissingBadge;
